import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalContent = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    text: 'Recopilamos información personal que usted nos proporciona directamente, como su nombre, dirección de correo electrónico e información de pago cuando realiza una compra. También podemos recopilar información automáticamente sobre su dispositivo y su interacción con nuestro sitio web.'
  }, {
    heading: '2. Uso de la Información',
    text: 'Utilizamos su información personal para procesar sus pedidos, enviarle el material digital adquirido, comunicarnos con usted sobre su compra y mejorar nuestros productos y servicios. No compartimos su información personal con terceros con fines de marketing.'
  }, {
    heading: '3. Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal contra el acceso no autorizado, la alteración, la divulgación o la destrucción. Todos los pagos se procesan a través de plataformas seguras y encriptadas.'
  }, {
    heading: '4. Cookies',
    text: 'Nuestro sitio web puede utilizar cookies y tecnologías similares para mejorar su experiencia de navegación y analizar el tráfico del sitio. Usted puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.'
  }, {
    heading: '5. Sus Derechos',
    text: 'Usted tiene derecho a acceder, rectificar o eliminar su información personal en cualquier momento. Para ejercer estos derechos, puede contactarnos a través de nuestro correo electrónico de soporte.'
  }, {
    heading: '6. Cambios en esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Le notificaremos sobre cambios significativos publicando la nueva política en nuestro sitio web.'
  }]
};
const termsContent = {
  title: 'Términos de Servicio',
  sections: [{
    heading: '1. Aceptación de los Términos',
    text: 'Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, usted acepta estar sujeto a estos términos de servicio. Si no está de acuerdo con alguno de estos términos, le rogamos que no utilice nuestro sitio.'
  }, {
    heading: '2. Productos Digitales',
    text: 'Los productos ofrecidos en este sitio son materiales digitales descargables, incluyendo eBooks, guías y ejercicios prácticos. Una vez completada la compra, recibirá acceso inmediato al material a través de su correo electrónico.'
  }, {
    heading: '3. Propiedad Intelectual',
    text: 'Todo el contenido, incluyendo textos, gráficos, logotipos, imágenes y material descargable, es propiedad exclusiva de Reconecta con tu Energía Femenina y está protegido por las leyes de propiedad intelectual. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.'
  }, {
    heading: '4. Política de Reembolso',
    text: 'Debido a la naturaleza digital de nuestros productos, todas las ventas son finales. No se ofrecen reembolsos una vez que el material ha sido entregado. Si experimenta problemas técnicos para acceder al contenido, nuestro equipo de soporte le asistirá.'
  }, {
    heading: '5. Limitación de Responsabilidad',
    text: 'El contenido proporcionado es de carácter educativo e informativo. No sustituye el consejo médico, psicológico o profesional. Los resultados pueden variar según cada persona. No nos hacemos responsables por decisiones tomadas basándose en el contenido de nuestros materiales.'
  }, {
    heading: '6. Contacto',
    text: 'Para cualquier consulta relacionada con estos términos de servicio, puede comunicarse con nosotros a través de nuestro correo electrónico de atención al cliente.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalContent>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-brown-dark text-cream/50 py-10 text-center text-sm" data-id="element-44">
        <div className="max-w-7xl mx-auto px-4" data-id="element-45">
          {/* Links */}
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-46">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/60 hover:text-gold transition-colors underline underline-offset-4 decoration-cream/20 hover:decoration-gold" data-id="element-47">
              Política de Privacidad
            </button>
            <span className="text-cream/20" data-id="element-48">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/60 hover:text-gold transition-colors underline underline-offset-4 decoration-cream/20 hover:decoration-gold" data-id="element-49">
              Términos de Servicio
            </button>
          </div>

          <p data-id="element-50">
            © {new Date().getFullYear()} Reconecta con tu Energía Femenina.
            Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-51">
        {activeModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 bg-brown-dark/70 backdrop-blur-sm z-50" onClick={() => setActiveModal(null)} data-id="element-52" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} transition={{
          duration: 0.35,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-cream rounded-3xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gold/20" data-id="element-53">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-gold/10 flex-shrink-0" data-id="element-54">
                <h3 className="text-2xl font-serif text-brown" data-id="element-55">
                  {content.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full bg-brown/5 hover:bg-brown/10 flex items-center justify-center transition-colors text-brown/60 hover:text-brown" data-id="element-56">
                  <XIcon className="w-5 h-5" data-id="element-57" />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto px-8 py-8 flex-1" data-id="element-58">
                <div className="space-y-8" data-id="element-59">
                  {content.sections.map((section, index) => <div key={index} data-id="element-60">
                      <h4 className="text-lg font-serif text-brown font-semibold mb-2" data-id="element-61">
                        {section.heading}
                      </h4>
                      <p className="text-brown/70 leading-relaxed" data-id="element-62">
                        {section.text}
                      </p>
                    </div>)}
                </div>

                <p className="mt-10 text-sm text-brown/40 text-center" data-id="element-63">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}